import { useCallback, useEffect, useState } from 'react';
import type { TabId } from '../components/Layout/BottomTabs';

const VALID_TABS: ReadonlySet<TabId> = new Set(['map', 'list', 'info']);
const LISTING_PREFIX = '/listing/';

/**
 * Snapshot of the URL state we care about. `vendorUid` is the stable
 * Sheets-assigned identifier that survives row reorders.
 */
export interface RouteState {
  tab: TabId;
  vendorUid: string | null;
}

function parsePath(pathname: string): RouteState {
  if (pathname.startsWith(LISTING_PREFIX)) {
    const raw = pathname.slice(LISTING_PREFIX.length);
    let uid: string;
    try {
      uid = decodeURIComponent(raw);
    } catch {
      return { tab: 'map', vendorUid: null };
    }
    return { tab: 'map', vendorUid: uid || null };
  }
  const segment = pathname.slice(1); // remove leading /
  const tab = (VALID_TABS.has(segment as TabId) ? segment : 'map') as TabId;
  return { tab, vendorUid: null };
}

function buildPath(state: RouteState): string {
  if (state.vendorUid) return `${LISTING_PREFIX}${encodeURIComponent(state.vendorUid)}`;
  if (state.tab === 'map') return '/';
  return `/${state.tab}`;
}

/**
 * Silently migrates old query-param URLs (?tab=list, ?vendor=abc) to the
 * new path-based scheme via replaceState so back-button history is clean.
 *
 * Intentionally called inside the useState lazy initializer rather than a
 * useEffect so the address bar never briefly shows the old URL on cold load
 * (a useEffect runs after first paint, leaving a visible flash for users
 * following old shared links). React StrictMode double-invokes initializers
 * in dev, but the second call is a no-op because the URL is already clean.
 */
function migrateLegacyUrl(): void {
  const params = new URLSearchParams(window.location.search);
  const vendor = params.get('vendor');
  const tab = params.get('tab');
  if (vendor) {
    window.history.replaceState(null, '', `${LISTING_PREFIX}${encodeURIComponent(vendor)}${window.location.hash}`);
  } else if (tab && VALID_TABS.has(tab as TabId)) {
    window.history.replaceState(null, '', `${tab === 'map' ? '/' : `/${tab}`}${window.location.hash}`);
  }
}

function pushUrl(next: RouteState) {
  const path = buildPath(next);
  const target = `${path}${window.location.hash}`;
  // Skip identical pushes to keep the back-button from filling with no-ops.
  if (target === `${window.location.pathname}${window.location.hash}`) {
    return;
  }
  window.history.pushState(null, '', target);
}

/**
 * Path-based router. Tab + vendor detail are deep-linkable and
 * back-button friendly. Dependency-free; replaces the in-memory tab
 * state we used to keep in AppShell.
 *
 * URL scheme:
 *   /            → map (default tab)
 *   /list        → list tab
 *   /info        → info tab
 *   /listing/<uid> → vendor detail (tab context intentionally not encoded)
 */
export function useRouteState() {
  const [state, setState] = useState<RouteState>(() => {
    if (typeof window === 'undefined') return { tab: 'map', vendorUid: null };
    migrateLegacyUrl();
    return parsePath(window.location.pathname);
  });

  useEffect(() => {
    function onPop() {
      setState(parsePath(window.location.pathname));
    }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const setTab = useCallback((tab: TabId) => {
    setState((prev) => {
      // No-op if the tab is unchanged — crucially, this preserves an
      // existing vendorUid. Mobile handlers call setTab('map') right
      // after openVendor(uid), so clearing-on-same-tab would wipe the
      // selection a beat after it landed.
      if (prev.tab === tab) return prev;
      // Preserve vendorUid when switching TO the map tab — that's the
      // "show-on-map" flow, where the caller has *just* set a vendor and
      // wants the marker focused. Clearing it here would wipe the
      // selection before ScaledMarkerLayer sees it (setTab and openVendor
      // are both queued setState updaters; setTab wins source-order and
      // overwrites vendorUid). For any other tab, close the overlay so
      // the URL stays clean.
      const next: RouteState = {
        tab,
        vendorUid: tab === 'map' ? prev.vendorUid : null,
      };
      pushUrl(next);
      return next;
    });
  }, []);

  const setVendorUid = useCallback((vendorUid: string | null) => {
    setState((prev) => {
      if (prev.vendorUid === vendorUid) return prev;
      const next: RouteState = { tab: prev.tab, vendorUid };
      pushUrl(next);
      return next;
    });
  }, []);

  return {
    tab: state.tab,
    vendorUid: state.vendorUid,
    setTab,
    setVendorUid,
  };
}

// Exported for tests + so AppShell can derive its discriminated ViewState.
export const __routerInternals = { parsePath, buildPath };
